import dayjs, {Dayjs} from 'dayjs';
import {IParticipationForms} from '../../react_query/tnn-participation-form/ParticipationForm';

export type IMultipleDateRangePayload = {
  start_date: string;
  end_date: string;
  operation_days: string[];
};

export type INeptuneRadioPayload = {
  season_type: string;
  opening_date?: string;
  operation_days?: string[];
  holidays?: string[];
  individual_days?: string[];
  multiple_date_ranges?: IMultipleDateRangePayload[];
  total_days: number;
  total_years: number;
};

export type ISerializedNeptuneRadioPayload = Omit<
  INeptuneRadioPayload,
  'operation_days' | 'holidays' | 'individual_days' | 'multiple_date_ranges'
> & {
  operation_days?: string;
  holidays?: string;
  individual_days?: string;
  multiple_date_ranges?: string;
};

type TSeasonalMode = 'one-day' | 'multiple-days' | null | undefined;

type TSeasonalRangeInput = {
  start_date: Dayjs | Date | string | null;
  end_date: Dayjs | Date | string | null;
  days_of_operation?: string[];
  isOneDay?: boolean;
};

type TBuildNeptuneRadioPayloadInput = {
  seasonType?: string;
  openingDate?: Dayjs | Date | string | null;
  totalDays?: number | null;
  totalYears?: number | null;
  daysOfOperation?: string[];
  holidayDates?: Array<Dayjs | Date | string | null>;
  seasonalDateRanges?: TSeasonalRangeInput[];
  seasonalMode?: TSeasonalMode;
  operationDays?: string | string[];
  holidays?: string | string[];
  individualDays?: string | string[];
  multipleDateRanges?: string | IMultipleDateRangePayload[];
};

const parseStringArray = (value?: string | string[]) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const parseMultipleDateRanges = (
  value?: string | IMultipleDateRangePayload[],
) => {
  if (!value) {
    return [];
  }

  if (Array.isArray(value)) {
    return value;
  }

  try {
    const parsedValue = JSON.parse(value);
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch {
    return [];
  }
};

const formatDate = (value?: Dayjs | Date | string | null) => {
  const formattedDate = dayjs(value);
  return formattedDate.isValid()
    ? formattedDate.format('YYYY-MM-DD')
    : undefined;
};

const getSeasonalPayload = ({
  individualDays,
  multipleDateRanges,
  seasonalDateRanges,
  seasonalMode,
}: Pick<
  TBuildNeptuneRadioPayloadInput,
  | 'individualDays'
  | 'multipleDateRanges'
  | 'seasonalDateRanges'
  | 'seasonalMode'
>) => {
  const parsedIndividualDays = parseStringArray(individualDays);
  const parsedMultipleDateRanges = parseMultipleDateRanges(multipleDateRanges);

  if (parsedIndividualDays.length > 0 || parsedMultipleDateRanges.length > 0) {
    return {
      individualDays: parsedIndividualDays,
      multipleDateRanges: parsedMultipleDateRanges,
    };
  }

  const normalizedRanges = (seasonalDateRanges ?? []).flatMap(range => {
    const startDate = dayjs(range.start_date);
    const endDate = dayjs(range.end_date);

    if (!startDate.isValid() || !endDate.isValid()) {
      return [];
    }

    const isOneDay = range.isOneDay ?? startDate.isSame(endDate, 'day');
    return [
      {
        startDate,
        endDate,
        isOneDay,
        daysOfOperation: range.days_of_operation ?? [],
      },
    ];
  });

  const filteredRanges =
    seasonalMode == null
      ? normalizedRanges
      : normalizedRanges.filter(
          range => range.isOneDay === (seasonalMode === 'one-day'),
        );

  return filteredRanges.reduce(
    (payload, range) => {
      if (range.isOneDay) {
        payload.individualDays.push(range.startDate.format('YYYY-MM-DD'));
      } else {
        payload.multipleDateRanges.push({
          start_date: range.startDate.format('YYYY-MM-DD'),
          end_date: range.endDate.format('YYYY-MM-DD'),
          operation_days: range.daysOfOperation,
        });
      }

      return payload;
    },
    {
      individualDays: [] as string[],
      multipleDateRanges: [] as IMultipleDateRangePayload[],
    },
  );
};

const getOperationDays = ({
  operationDays,
  daysOfOperation,
}: Pick<
  TBuildNeptuneRadioPayloadInput,
  'operationDays' | 'daysOfOperation'
>) => {
  if (operationDays) {
    return parseStringArray(operationDays);
  }

  return daysOfOperation ?? [];
};

const getHolidayStrings = ({
  holidays,
  holidayDates,
}: Pick<TBuildNeptuneRadioPayloadInput, 'holidays' | 'holidayDates'>) => {
  if (holidays) {
    return parseStringArray(holidays);
  }

  return (holidayDates ?? []).flatMap(date => {
    const formattedDate = formatDate(date);
    return formattedDate ? [formattedDate] : [];
  });
};

export const buildNeptuneRadioPayload = (
  input: TBuildNeptuneRadioPayloadInput,
): INeptuneRadioPayload => {
  const payload: INeptuneRadioPayload = {
    season_type: input.seasonType ?? '',
    total_days: input.totalDays ?? 0,
    total_years: input.totalYears ?? 0,
  };

  if (input.seasonType === 'Seasonally') {
    const {individualDays, multipleDateRanges} = getSeasonalPayload(input);

    if (individualDays.length > 0) {
      payload.individual_days = individualDays;
    }

    if (multipleDateRanges.length > 0) {
      payload.multiple_date_ranges = multipleDateRanges;
    }

    payload.opening_date =
      formatDate(input.openingDate) ??
      individualDays[0] ??
      multipleDateRanges[0]?.start_date;
  }

  if (input.seasonType === 'All Year') {
    payload.opening_date = formatDate(input.openingDate);

    const operationDays = getOperationDays(input);
    if (operationDays.length > 0) {
      payload.operation_days = operationDays;
    }

    const holidayStrings = getHolidayStrings(input);
    if (holidayStrings.length > 0) {
      payload.holidays = holidayStrings;
    }
  }

  return payload;
};

export const buildNeptuneRadioPayloadFromForm = (
  participationFormDetails: IParticipationForms,
) => {
  return buildNeptuneRadioPayload({
    seasonType: participationFormDetails.season_type,
    openingDate: participationFormDetails.opening_date,
    totalDays: participationFormDetails.total_days,
    totalYears: participationFormDetails.total_years,
    daysOfOperation: participationFormDetails.days_of_operation,
    holidayDates: participationFormDetails.holiday_dates,
    seasonalDateRanges: participationFormDetails.seasonal_date_ranges,
    operationDays: participationFormDetails.operation_days,
    holidays: participationFormDetails.holidays,
    individualDays: participationFormDetails.individual_days,
    multipleDateRanges: participationFormDetails.multiple_date_ranges,
  });
};

export const serializeNeptuneRadioPayload = (
  payload: INeptuneRadioPayload,
): ISerializedNeptuneRadioPayload => {
  return {
    ...payload,
    operation_days: payload.operation_days
      ? JSON.stringify(payload.operation_days)
      : undefined,
    holidays: payload.holidays ? JSON.stringify(payload.holidays) : undefined,
    individual_days: payload.individual_days
      ? JSON.stringify(payload.individual_days)
      : undefined,
    multiple_date_ranges: payload.multiple_date_ranges
      ? JSON.stringify(payload.multiple_date_ranges)
      : undefined,
  };
};

export const appendNeptuneRadioPayloadToFormData = (
  formData: FormData,
  payload: INeptuneRadioPayload,
) => {
  const entries = Object.entries(payload) as Array<
    [
      keyof INeptuneRadioPayload,
      INeptuneRadioPayload[keyof INeptuneRadioPayload],
    ]
  >;

  entries.forEach(([key, value]) => {
    if (value === undefined) {
      return;
    }

    if (Array.isArray(value)) {
      formData.append(key, JSON.stringify(value));
      return;
    }

    formData.append(key, value.toString());
  });
};
